import { bind } from '../../../../../shared/helpers/decorators';
import { EventEmitter } from '../../../../../shared/helpers/event-emitter';
import { onStreamStartCallback } from '../../../../../shared/helpers/host-api';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { FileRecorder, FileRecorderCloseResult } from './types';

const logger = createLogger('ws-recorder');

interface RecorderOptions {
    url: string;
}

export class WSRecorder extends EventEmitter implements FileRecorder {
    private readonly ws: WebSocket;
    private readonly options: RecorderOptions;
    private chunkNumber: number;

    constructor(options: RecorderOptions) {
        super();

        this.options = options;
        this.ws = new WebSocket(options.url);
        this.ws.addEventListener('message', this.handleMessage);
        this.ws.addEventListener('error', this.handleError);
        this.ws.addEventListener('close', this.handleClose);
        this.chunkNumber = 0;
    }

    @bind
    init() {
        return Promise.resolve();
    }

    @bind
    private handleMessage(event: MessageEvent) {
        const data = JSON.parse(event.data);

        if (typeof onStreamStartCallback === 'function' && ['ok', 'error'].includes(data.status)) {
            onStreamStartCallback(data);
        }

        if (data.status === 'restart') {
            this.emit('restart');
        }
    }

    @bind
    private handleError(event: Event) {
        logger.error(new Error('WebSocket error during recording upload'), {
            method: 'handleError',
            additional: {
                stopReason: 'websocket_error',
                wsReadyState: this.ws.readyState,
                wsUrl: this.options.url,
                chunkNumber: this.chunkNumber,
                eventType: event.type,
            },
        });
    }

    @bind
    private handleClose(event: CloseEvent) {
        logger.event('WebSocket closed during recording upload', {
            additional: {
                method: 'handleClose',
                stopReason: 'websocket_close',
                wsCode: event.code,
                wsReason: event.reason,
                wasClean: event.wasClean,
                chunkNumber: this.chunkNumber,
            },
        });
    }

    addChunk(chunk: Blob): Promise<void> {
        this.chunkNumber++;
        this.ws.send(chunk);
        // eslint-disable-next-line no-console
        console.log(`Sending chunk ${this.chunkNumber}`);
        return Promise.resolve();
    }

    close(): Promise<FileRecorderCloseResult> {
        this.ws.removeEventListener('error', this.handleError);
        this.ws.removeEventListener('close', this.handleClose);
        this.ws.close();
        return Promise.resolve({ status: 'saved' });
    }
}
